// src/auth/KeycloakProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { KeycloakError, KeycloakInstance, KeycloakInitOptions } from 'keycloak-js';

type KeycloakEvent =
  | 'onReady'
  | 'onInitError'
  | 'onAuthSuccess'
  | 'onAuthError'
  | 'onAuthRefreshSuccess'
  | 'onAuthRefreshError'
  | 'onAuthLogout'
  | 'onTokenExpired';

type KeycloakContextShape = {
  keycloak: KeycloakInstance;
  initialized: boolean;
};

type KeycloakProviderProps = {
  authClient?: KeycloakInstance;
  client?: KeycloakInstance;
  children: ReactNode;
  initOptions?: KeycloakInitOptions;
  autoRefreshToken?: boolean;
  LoadingComponent?: ReactNode;
  loadingComponent?: ReactNode;
  isLoadingCheck?: (keycloak: KeycloakInstance) => boolean;
  onEvent?: (event: KeycloakEvent, error?: KeycloakError) => void;
};

const KeycloakContext = createContext<KeycloakContextShape | undefined>(undefined);

const isDebugLoggingEnabled = process.env.NODE_ENV !== 'production';

const debugLog = (...message: unknown[]) => {
  if (isDebugLoggingEnabled) {
    // eslint-disable-next-line no-console
    console.debug('[KeycloakProvider]', ...message);
  }
};

export const useKeycloak = () => {
  const ctx = useContext(KeycloakContext);

  debugLog('useKeycloak hook accessed', { initialized: ctx?.initialized });

  if (!ctx) {
    throw new Error('useKeycloak must be used inside KeycloakProvider');
  }

  return ctx;
};

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({
  authClient,
  client,
  children,
  initOptions,
  autoRefreshToken = true,
  LoadingComponent,
  loadingComponent,
  isLoadingCheck,
  onEvent,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [hasInitError, setHasInitError] = useState(false);
  const keycloakClient = authClient ?? client;
  const resolvedLoadingComponent = LoadingComponent ?? loadingComponent;
  const initOptionsKey = useMemo(() => {
    return JSON.stringify(initOptions || {});
  }, [initOptions]);
  const memoizedInitOptions = useMemo(() => {
    return JSON.parse(initOptionsKey) as KeycloakInitOptions;
  }, [initOptionsKey]);
  const memoizedLoadingCheck = useMemo(() => {
    return isLoadingCheck;
  }, [isLoadingCheck]);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  if (!keycloakClient) {
    throw new Error('KeycloakProvider requires an authClient instance');
  }

  useEffect(() => {
    debugLog('initialising Keycloak', memoizedInitOptions);

    const mergedInitOptions: KeycloakInitOptions = {
      onLoad: 'check-sso',
      ...memoizedInitOptions,
    };

    keycloakClient.onReady = () => {
      debugLog('event:onReady');
      onEventRef.current?.('onReady');
      setInitialized(true);
    };
    keycloakClient.onAuthSuccess = () => {
      debugLog('event:onAuthSuccess');
      onEventRef.current?.('onAuthSuccess');
    };
    keycloakClient.onAuthError = (authError) => {
      debugLog('event:onAuthError', authError);
      onEventRef.current?.('onAuthError', authError);
    };
    keycloakClient.onAuthRefreshSuccess = () => {
      debugLog('event:onAuthRefreshSuccess');
      onEventRef.current?.('onAuthRefreshSuccess');
    };
    keycloakClient.onAuthRefreshError = () => {
      debugLog('event:onAuthRefreshError');
      onEventRef.current?.('onAuthRefreshError');
    };
    keycloakClient.onAuthLogout = () => {
      debugLog('event:onAuthLogout');
      onEventRef.current?.('onAuthLogout');
    };

    keycloakClient.onTokenExpired = async () => {
      debugLog('event:onTokenExpired');
      onEventRef.current?.('onTokenExpired');
      if (!autoRefreshToken) return;

      try {
        const refreshed = await keycloakClient.updateToken(30);

        if (refreshed) {
          debugLog('token refresh success');
          onEventRef.current?.('onAuthRefreshSuccess');
        }
      } catch (refreshError) {
        debugLog('token refresh error', refreshError);
        onEventRef.current?.('onAuthRefreshError', refreshError as KeycloakError);
      }
    };

    keycloakClient.init(mergedInitOptions).catch((initError) => {
      debugLog('init error', initError);
      setHasInitError(true);
      onEventRef.current?.('onInitError', initError);
    });

    return () => {
      keycloakClient.onReady = undefined;
      keycloakClient.onAuthSuccess = undefined;
      keycloakClient.onAuthError = undefined;
      keycloakClient.onAuthRefreshSuccess = undefined;
      keycloakClient.onAuthRefreshError = undefined;
      keycloakClient.onAuthLogout = undefined;
      keycloakClient.onTokenExpired = undefined;
    };
  }, [autoRefreshToken, keycloakClient, memoizedInitOptions]);

  const isLoading =
    !initialized || (memoizedLoadingCheck ? memoizedLoadingCheck(keycloakClient) : false);

  const contextValue = useMemo(() => {
    return { keycloak: keycloakClient, initialized };
  }, [keycloakClient, initialized]);

  if (isLoading && resolvedLoadingComponent) {
    return <>{resolvedLoadingComponent}</>;
  }

  if (hasInitError) {
    return resolvedLoadingComponent ? <>{resolvedLoadingComponent}</> : null;
  }

  return <KeycloakContext.Provider value={contextValue}>{children}</KeycloakContext.Provider>;
};

export const ReactKeycloakProvider = KeycloakProvider;
